import {
  PAYMENT_METHOD,
  TRANSACTION_PARTY_TYPE,
  TRANSACTION_REQUISITES_TAB,
  TRANSACTION_TYPES
} from 'components/constants';

export const BENEFICIARY_TYPE = {
  CORPORATE: 'CORPORATE',
  INDIVIDUAL: 'INDIVIDUAL'
};

export const isExternalPaymentMethod = (paymentMethod) =>
  !!paymentMethod && paymentMethod !== PAYMENT_METHOD.INTERNAL && paymentMethod !== PAYMENT_METHOD.CRYPTO;

export const isAdvancedPaymentMethod = (paymentMethod) =>
  [PAYMENT_METHOD.SWIFT, PAYMENT_METHOD.TARGET2].includes(paymentMethod);

export const createCustomCommission = (currency) => ({
  tariffId: null,
  amount: '',
  currency: currency || 'EUR',
  label: 'Custom Commission',
  isCustom: true
});

// The prefill can carry several requisites kinds at once - open the tab of the most specific one
const getRequisitesTab = (requisites) => {
  if (requisites.iban) {
    return TRANSACTION_REQUISITES_TAB.IBAN;
  }
  if (requisites.accountNumber && requisites.sortCode) {
    return TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT;
  }
  if (requisites.accountNumber) {
    return TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES;
  }
  return TRANSACTION_REQUISITES_TAB.IBAN;
};

// `original` of the repeat endpoint. Its sender and internal recipient are wallet numbers, the form needs their
// accounts. `checkedIbanBank` is the IBAN check response of its IBAN (null when it failed).
export const fromRepeatDto = (original, senderAccount, recipientAccount, checkedIbanBank) => {
  const beneficiary = original.beneficiary || {};
  // An internal recipient is the wallet only - bank details must not drive the bank country (commission, payment
  // purpose)
  const requisites = original.paymentMethod === PAYMENT_METHOD.INTERNAL ? {} : original.requisites || {};
  // The original bank details are of the account in any case. The IBAN ones are the bank data of its check, or the
  // original ones when the check failed.
  const ibanBank = requisites.iban ? checkedIbanBank || requisites : {};

  return {
    transactionType: TRANSACTION_TYPES.OUTGOING,
    senderAccount,
    paymentProvider: senderAccount?.transfer_provider || '',
    paymentMethod: original.paymentMethod || '',
    currency: senderAccount?.currency || '',
    amount: original.amount !== null && original.amount !== undefined ? String(original.amount) : '',
    // The original commission is informational, its tariffs have no ids - the commission is calculated in the form
    commissions: [createCustomCommission(senderAccount?.currency)],
    // All documents of the original transaction are copied to the new one, unless the operator leaves some out
    originalDocuments: (original.documents || []).map((document) => document.id),
    recipientAccount,
    partyType:
      beneficiary.type === BENEFICIARY_TYPE.INDIVIDUAL
        ? TRANSACTION_PARTY_TYPE.INDIVIDUAL
        : TRANSACTION_PARTY_TYPE.COMPANY,
    partyFirstName: beneficiary.firstName || '',
    partyLastName: beneficiary.lastName || '',
    partyCompanyName: beneficiary.companyName || '',
    registrationNumber: beneficiary.registrationNumber || '',
    partyCountry: beneficiary.country || null,
    partyAddress: beneficiary.address || '',
    partyCity: beneficiary.city || '',
    partyPostCode: beneficiary.postCode || '',
    partyState: beneficiary.state || '',
    activePartyTab: getRequisitesTab(requisites),
    iban: requisites.iban || '',
    ibanBankCountry: ibanBank.bankCountry || null,
    ibanBankName: ibanBank.bankName || '',
    ibanBic: ibanBank.bic || '',
    accountNumber: requisites.accountNumber || '',
    sortCode: requisites.sortCode || '',
    bankCountry: requisites.bankCountry || null,
    bankName: requisites.bankName || '',
    bic: requisites.bic || '',
    recipientWalletAddress: requisites.cryptoAddress || '',
    cryptoNetwork: requisites.cryptoNetwork || '',
    payment_purpose: original.purpose || null,
    comment: original.comment || '',
    commentToAdmin: original.commentToAdmin || ''
  };
};

const omitEmpty = (object) => Object.fromEntries(Object.entries(object).filter(([, value]) => !!value));

// Only the requisites of the recipient kind are sent, for a bank account - the account of the active tab with the bank
// details (required for a non IBAN account only). Only filled fields are sent, they are stored on the new transaction.
const toRequisites = (data) => {
  if (data.paymentMethod === PAYMENT_METHOD.INTERNAL) {
    return { walletNumber: data.recipientAccount?.wallet_number };
  }

  if (data.paymentMethod === PAYMENT_METHOD.CRYPTO) {
    return { cryptoAddress: data.recipientWalletAddress, cryptoNetwork: data.cryptoNetwork };
  }

  const bank = { bankCountry: data.bankCountry, bankName: data.bankName, bic: data.bic };

  switch (data.activePartyTab) {
    case TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT:
      return omitEmpty({ accountNumber: data.accountNumber, sortCode: data.sortCode, ...bank });
    case TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES:
      return omitEmpty({ accountNumber: data.accountNumber, ...bank });
    default:
      return omitEmpty({
        iban: data.iban,
        bankCountry: data.ibanBankCountry,
        bankName: data.ibanBankName,
        bic: data.ibanBic
      });
  }
};

// Country of the recipient bank: of the IBAN or of the account, by the requisites kind. Only an external fiat
// recipient has a bank.
export const getBankCountry = (data) => {
  if (!isExternalPaymentMethod(data.paymentMethod)) {
    return null;
  }

  return data.activePartyTab === TRANSACTION_REQUISITES_TAB.IBAN ? data.ibanBankCountry : data.bankCountry;
};

// Only an external fiat recipient has a beneficiary, for the others it is ignored by the backend. The repeat form shows
// the whole beneficiary address for any external payment method, only filled fields are sent. Registration number is
// a corporate attribute only.
const toBeneficiary = (data) => {
  if (!isExternalPaymentMethod(data.paymentMethod)) {
    return undefined;
  }

  const isCompany = data.partyType === TRANSACTION_PARTY_TYPE.COMPANY;

  return omitEmpty({
    ...(isCompany
      ? { type: BENEFICIARY_TYPE.CORPORATE, companyName: data.partyCompanyName }
      : { type: BENEFICIARY_TYPE.INDIVIDUAL, firstName: data.partyFirstName, lastName: data.partyLastName }),
    registrationNumber: isCompany ? data.registrationNumber : null,
    country: data.partyCountry,
    address: data.partyAddress,
    city: data.partyCity,
    postCode: data.partyPostCode,
    state: data.partyState
  });
};

// A commission without an amount is not sent - it is an empty row of the form
const isSentCommission = (commission) => !!commission.amount;

// Body of the repeat check and confirm. `attachedDocuments` are the new uploads, the kept documents of the original
// transaction are copied by the backend.
export const toDto = (data, attachedDocuments) => ({
  senderWallet: data.senderAccount?.wallet_number,
  beneficiary: toBeneficiary(data),
  requisites: toRequisites(data),
  amount: Number(data.amount),
  paymentMethod: data.paymentMethod,
  ...(data.payment_purpose ? { purpose: data.payment_purpose } : {}),
  comment: data.comment,
  commentToAdmin: data.commentToAdmin,
  tariffs:
    data.commissions?.filter(isSentCommission).map((c) => ({
      ...(c.tariffId ? { tariffId: c.tariffId } : {}),
      amount: Number(c.amount)
    })) || [],
  originalDocuments: data.originalDocuments || [],
  attachedDocuments: attachedDocuments.map((d) => d.id)
});

// Form fields of the error paths. `tariffs[i]` is a commission row, `originalDocuments[i]` and `attachedDocuments[i]`
// are documents, the other paths (`root`, a whole `beneficiary`, `requisites`, `tariffs` or a documents list,
// `beneficiary.type`) belong to no field.
const ERROR_PATH_FIELDS = {
  senderWallet: 'senderAccount',
  paymentMethod: 'paymentMethod',
  amount: 'amount',
  purpose: 'payment_purpose',
  comment: 'comment',
  commentToAdmin: 'commentToAdmin',
  'beneficiary.companyName': 'partyCompanyName',
  'beneficiary.firstName': 'partyFirstName',
  'beneficiary.lastName': 'partyLastName',
  'beneficiary.registrationNumber': 'registrationNumber',
  'beneficiary.country': 'partyCountry',
  'beneficiary.address': 'partyAddress',
  'beneficiary.city': 'partyCity',
  'beneficiary.postCode': 'partyPostCode',
  'beneficiary.state': 'partyState',
  'requisites.walletNumber': 'recipientAccount',
  'requisites.iban': 'iban',
  'requisites.bankCountry': 'bankCountry',
  'requisites.bankName': 'bankName',
  'requisites.accountNumber': 'accountNumber',
  'requisites.sortCode': 'sortCode',
  'requisites.bic': 'bic',
  'requisites.cryptoAddress': 'recipientWalletAddress',
  'requisites.cryptoNetwork': 'cryptoNetwork'
};

// The bank details of an IBAN are separate fields of the form
const IBAN_ERROR_PATH_FIELDS = {
  ...ERROR_PATH_FIELDS,
  'requisites.bankCountry': 'ibanBankCountry',
  'requisites.bankName': 'ibanBankName',
  'requisites.bic': 'ibanBic'
};

const LIST_ITEM_ERROR_PATH = /^(\w+)\[(\d+)]/;

const DOCUMENTS_LISTS = ['originalDocuments', 'attachedDocuments'];

// `errors` of the repeat endpoints: { path: [{ code, message }] }. They are split into the errors of the form fields
// (in the shape of the form errors) and the common ones, that belong to no field. `dto` is the checked request (or
// `original` of the repeat endpoint) and `commissions` are the form rows it was made of - `tariffs[i]` of the request
// is the i-th sent one. Errors of the documents are keyed by the document id:
// { originalDocuments: { id: message }, attachedDocuments: { id: message } }.
export const fromErrorsDto = (errors, dto = {}, commissions = []) => {
  const tariffRows = commissions.flatMap((commission, index) => (isSentCommission(commission) ? [index] : []));
  const pathFields = dto.requisites?.iban ? IBAN_ERROR_PATH_FIELDS : ERROR_PATH_FIELDS;
  const fields = {};
  const common = [];

  Object.entries(errors || {}).forEach(([path, pathErrors]) => {
    const message = (pathErrors || []).map((error) => error.message || error.code).join('; ');
    const field = pathFields[path];
    const [, list, index] = path.match(LIST_ITEM_ERROR_PATH) || [];
    const tariffRow = list === 'tariffs' ? tariffRows[index] : undefined;
    const documentId = DOCUMENTS_LISTS.includes(list) ? dto[list]?.[index] : undefined;

    if (field) {
      fields[field] = message;
    } else if (tariffRow !== undefined) {
      // A commission row has only an amount to show the error on
      fields.commissions = fields.commissions || [];
      fields.commissions[tariffRow] = { amount: message };
    } else if (documentId) {
      fields[list] = { ...fields[list], [documentId]: message };
    } else {
      common.push({ path, message });
    }
  });

  return { fields, common };
};
